import React, { useEffect, useState } from "react";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../../../../../app/hooks";
import { savedAsiaPayoutData } from "../../../../redux/formDataSlice";
import { fetchBankList } from "../../../../redux/slice";
import { merchantBusinessKeys } from "../../../../../Settings/settingsSlice";
import { ContentWrapper, InputWrapper, Error, ButtonsWrapper } from "../../../shared/styles";
import { Input } from "../../../../../../components/common/Input";
import CustomSelect from "../../../../../../components/common/CustomSelect";
import { Button } from "../../../../../../components/common/Button";
import Loader from "../../../../../../components/common/Loader";
import { PHP_BENEFICIARY_TYPES, PHP_DATA_SLICE_KEY } from "../const";
import { PhilippinesFormInputs, PhilippinesStepFormProps } from "../types";

type Bank = { code: string; name: string };

const PhilippinesBeneficiaryForm: React.FC<PhilippinesStepFormProps> = ({
  handleNext,
  handlePrevious,
}) => {
  const dispatch = useAppDispatch();
  const asiaPayoutData: PhilippinesFormInputs = useAppSelector(
    (state) => (state.formData as any)?.asiaPayoutData?.[PHP_DATA_SLICE_KEY] || {}
  );
  const { keys } = useAppSelector((state) => state.settings);
  const { businessId } = useAppSelector((state) => state.users);
  const { loading } = useAppSelector((state) => state?.wallets || {});

  const [banksList, setBanksList] = useState<Bank[]>([]);

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors, isValid },
  } = useForm<PhilippinesFormInputs>({
    defaultValues: asiaPayoutData,
  });

  useEffect(() => {
    if (businessId) {
      dispatch(merchantBusinessKeys(businessId));
    }
  }, [businessId, dispatch]);

  useEffect(() => {
    if (!keys?.data?.publicKey) return;
    dispatch(fetchBankList({ country: "PHP", xAuthToken: keys.data.publicKey })).then(
      (action) => {
        if (fetchBankList.fulfilled.match(action)) {
          const uniqueBanks = Array.from(
            new Map(
              ((action.payload?.data || []) as Bank[]).map((bank) => [bank.name, bank])
            ).values()
          );
          setBanksList(uniqueBanks);
        }
      }
    );
  }, [dispatch, keys?.data?.publicKey]);

  const onSubmit: SubmitHandler<PhilippinesFormInputs> = (data) => {
    dispatch(
      savedAsiaPayoutData({ country: PHP_DATA_SLICE_KEY, data: { ...asiaPayoutData, ...data } })
    );
    handleNext?.();
  };

  const bankOptions = banksList.map((bank) => ({ value: bank.name, label: bank.name }));

  return (
    <ContentWrapper>
      <Loader isLoading={loading} />

      <InputWrapper>
        <Controller
          name="beneficiaryType"
          control={control}
          rules={{ required: "Beneficiary type is required" }}
          render={({ field }) => (
            <CustomSelect
              $width="100%"
              placeholder="Beneficiary type"
              options={PHP_BENEFICIARY_TYPES}
              value={field.value || undefined}
              onChange={field.onChange}
              lightBorder
            />
          )}
        />
        {errors?.beneficiaryType?.message && (
          <Error>{String(errors.beneficiaryType.message)}</Error>
        )}
      </InputWrapper>

      <InputWrapper>
        <Controller
          name="accountName"
          control={control}
          rules={{ required: "Full name is required" }}
          render={({ field }) => (
            <Input
              type="text"
              label="Full name"
              value={field.value || ""}
              onChange={field.onChange}
              hasError={!!errors?.accountName}
            />
          )}
        />
        {errors?.accountName?.message && <Error>{String(errors.accountName.message)}</Error>}
      </InputWrapper>

      <InputWrapper>
        <Controller
          name="bankName"
          control={control}
          rules={{ required: "Bank name is required" }}
          render={({ field }) => (
            <CustomSelect
              value={field.value || undefined}
              options={bankOptions}
              onChange={(selectedValue) => {
                field.onChange(selectedValue);
                const selectedBank = banksList.find((b) => b.name === selectedValue);
                setValue("bankCode", selectedBank?.code || "");
              }}
              $width="100%"
              placeholder="Bank name"
              showSearch
              filterOption={(inputValue, option) =>
                option.label.toLowerCase().includes(inputValue.toLowerCase())
              }
              lightBorder
            />
          )}
        />
        {errors?.bankName?.message && <Error>{String(errors.bankName.message)}</Error>}
      </InputWrapper>

      <InputWrapper>
        <Controller
          name="accountNumber"
          control={control}
          rules={{
            required: "Account number is required",
            pattern: { value: /^\d+$/, message: "Account number must contain only digits" },
          }}
          render={({ field }) => (
            <Input
              type="text"
              inputMode="numeric"
              label="Account number"
              value={field.value || ""}
              onChange={(event: any) => field.onChange(event.target.value.replace(/\D/g, ""))}
              hasError={!!errors?.accountNumber}
            />
          )}
        />
        {errors?.accountNumber?.message && (
          <Error>{String(errors.accountNumber.message)}</Error>
        )}
      </InputWrapper>

      <InputWrapper>
        <Controller
          name="narration"
          control={control}
          rules={{
            required: "Narration is required",
            validate: (value) => !!value?.trim() || "Narration is required",
          }}
          render={({ field }) => (
            <Input
              type="text"
              label="Narration"
              value={field.value || ""}
              onChange={field.onChange}
              hasError={!!errors?.narration}
            />
          )}
        />
        {errors?.narration?.message && <Error>{String(errors.narration.message)}</Error>}
      </InputWrapper>

      <ButtonsWrapper>
        <Button onClick={handlePrevious} theme="alternate" height="48px" width="203px">
          Back
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          disabled={!isValid}
          label="Continue"
          height="48px"
        />
      </ButtonsWrapper>
    </ContentWrapper>
  );
};

export default PhilippinesBeneficiaryForm;
